import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { environment } from './environment';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, ChevronRightIcon, ChevronLeftIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { renderFieldValue, formatFieldName } from './utils';

// TEST LOG - If you see this, the file is loading
console.log('=== SESSIONS.JSX FILE LOADED ===');
console.log('File version:', Date.now());
console.log('========================');

// Helper function to get token
function getToken() {
  return localStorage.getItem("token");
}

// Error Boundary Component
class SessionsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Sessions Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full flex flex-col items-center justify-center p-8">
          <div className="text-red-600 text-lg mb-4">Something went wrong with the Sessions component</div>
          <div className="text-slate-600 mb-4">Error: {this.state.error?.message}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const Sessions = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewSession, setShowNewSession] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  
  // Debug: Log whenever selectedType changes
  useEffect(() => {
    console.log('=== SELECTED TYPE CHANGED ===');
    console.log('selectedType changed to:', selectedType);
    console.log('selectedType type:', typeof selectedType);
    console.log('selectedType length:', selectedType ? selectedType.length : 'undefined');
    console.log('========================');
  }, [selectedType]);
  const [selectedClient, setSelectedClient] = useState('');
  
  // Real EMR types from API
  const [emrTypes, setEmrTypes] = useState([]);
  const [loadingEmrTypes, setLoadingEmrTypes] = useState(false);
  const [emrTypesError, setEmrTypesError] = useState('');
  
  // EMR Fields from API (for dropdown values)
  const [emrFields, setEmrFields] = useState([]);
  const [loadingEmrFields, setLoadingEmrFields] = useState(false);
  const [emrFieldsError, setEmrFieldsError] = useState('');
  
  // Real clients from API
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState('');

  // EMR Type Results for dynamic field generation
  const [emrTypeResults, setEmrTypeResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [resultsError, setResultsError] = useState('');

  // Manual Fields from API
  const [manualFields, setManualFields] = useState([]);
  const [loadingManualFields, setLoadingManualFields] = useState(false);
  const [manualFieldsError, setManualFieldsError] = useState('');

  // Session form data
  const [sessionForm, setSessionForm] = useState({
    // Common field
    instructions: ''
  });

  // Session creation state
  const [creatingSession, setCreatingSession] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [sessionSuccess, setSessionSuccess] = useState('');

  // Tab management
  const [activeTab, setActiveTab] = useState('');
  const [sessionTabs, setSessionTabs] = useState([]);

  // Fields to Display feature state
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [visibleFields, setVisibleFields] = useState([]);
  const [selectedAvailableField, setSelectedAvailableField] = useState(null);
  const [selectedVisibleField, setSelectedVisibleField] = useState(null);
  const [fieldDisplayConfig, setFieldDisplayConfig] = useState({});

  // Fetch Sessions
  const fetchSessions = async () => {
    try {
      const response = await fetch(`${environment.apiUrl}/sessions`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('=== SESSIONS FETCHED ===');
        console.log('Raw sessions data:', data);
        console.log('Sessions count:', data.length);
        console.log('Is data an array?', Array.isArray(data));
        console.log('Data type:', typeof data);
        if (Array.isArray(data) && data.length > 0) {
          console.log('First session client_id_name:', data[0].client_id_name);
          console.log('First session client_id:', data[0].client_id);
          console.log('All sessions client_id_name values:', data.map(s => ({ id: s.id, client_id_name: s.client_id_name, emr_name: s.emr_name })));
        }
        if (Array.isArray(data)) {
          console.log('Session types found:', data.map(s => ({ id: s.emr_type_id, name: s.emr_type_name })));
        } else {
          console.log('Data is not an array, structure:', Object.keys(data));
        }
        setSessions(data);
        
        // Create tabs based on unique EMR types
        console.log('=== TAB CREATION DEBUG ===');
        console.log('All emr_name values:', data.map(s => s.emr_name));
        console.log('After safeString:', data.map(s => safeString(s.emr_name)));
        console.log('After filtering:', data.map(s => safeString(s.emr_name)).filter(type => type !== 'Unknown Type'));
        
        // More robust deduplication with case-insensitive comparison and trimming
        const emrNames = data.map(session => safeString(session.emr_name)).filter(type => type !== 'Unknown Type');
        const uniqueTypes = emrNames.filter((type, index, self) => 
          self.findIndex(t => t.toLowerCase().trim() === type.toLowerCase().trim()) === index
        );
        console.log('Final unique types for tabs:', uniqueTypes);
        console.log('========================');
        setSessionTabs(uniqueTypes);
        
        // Set first tab as active if no active tab
        if (uniqueTypes.length > 0 && !activeTab) {
          setActiveTab(uniqueTypes[0]);
        }
        console.log('Active tab set to:', uniqueTypes.length > 0 && !activeTab ? uniqueTypes[0] : activeTab);
        console.log('========================');
      } else {
        console.error('Failed to fetch sessions');
      }
    } catch (err) {
      console.error('Network error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch EMR Types
  const fetchEmrTypes = async () => {
    setLoadingEmrTypes(true);
    setEmrTypesError('');
    try {
      const response = await fetch(`${environment.apiUrl}/emr-types/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('EMR Types fetched:', data);
        console.log('EMR Type names:', data.map(type => type.name));
        setEmrTypes(data);
      } else {
        setEmrTypesError('Failed to fetch EMR types');
      }
    } catch (err) {
      setEmrTypesError('Network error');
    } finally {
      setLoadingEmrTypes(false);
    }
  };

  // Fetch EMR Fields (for dropdown values)
  const fetchEmrFields = async () => {
    setLoadingEmrFields(true);
    setEmrFieldsError('');
    try {
      const response = await fetch(`${environment.apiUrl}/emr-types-fields/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('EMR Fields fetched:', data);
        setEmrFields(data);
      } else {
        setEmrFieldsError('Failed to fetch EMR fields');
      }
    } catch (err) {
      setEmrFieldsError('Network error');
    } finally {
      setLoadingEmrFields(false);
    }
  };

  // Fetch Clients
  const fetchClients = async () => {
    setLoadingClients(true);
    setClientsError('');
    try {
      const response = await fetch(`${environment.apiUrl}/api/Clients`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Clients fetched:', data);
        setClients(data);
      } else {
        setClientsError('Failed to fetch clients');
      }
    } catch (err) {
      setClientsError('Network error');
    } finally {
      setLoadingClients(false);
    }
  };

  // Fetch EMR Type Results for dynamic field generation
  const fetchEmrTypeResults = async () => {
    setLoadingResults(true);
    setResultsError('');
    try {
      // First, let's try to get results for all EMR types
      const response = await fetch(`${environment.apiUrl}/results`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched EMR type results:', data);
        setEmrTypeResults(data);
      } else {
        console.error('Failed to fetch EMR type results:', response.status);
        setResultsError('Failed to fetch EMR type results');
      }
    } catch (err) {
      console.error('Error fetching EMR type results:', err);
      setResultsError('Network error');
    } finally {
      setLoadingResults(false);
    }
  };

  // Fetch Manual Fields
  const fetchManualFields = async () => {
    setLoadingManualFields(true);
    setManualFieldsError('');
    try {
      const response = await fetch(`${environment.apiUrl}/manual-fields/`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Manual fields fetched:', data);
        setManualFields(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to fetch manual fields:', response.status, errorData);
        
        // Enhanced error handling for manual fields fetch
        let errorMessage = 'Failed to fetch manual fields';
        
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map(err => err.msg || err.message || 'Validation error').join(', ');
          } else if (typeof errorData.detail === 'object') {
            errorMessage = errorData.detail.msg || errorData.detail.message || 'Validation error';
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
        
        setManualFieldsError(errorMessage);
      }
    } catch (err) {
      console.error('Network error fetching manual fields:', err);
      setManualFieldsError('Network error: ' + err.message);
    } finally {
      setLoadingManualFields(false);
    }
  };

  useEffect(() => {
    // Load field display configuration from localStorage
    const savedConfig = localStorage.getItem('fieldDisplayConfig');
    if (savedConfig) {
      try {
        setFieldDisplayConfig(JSON.parse(savedConfig));
      } catch (error) {
        console.error('Error parsing field display config:', error);
      }
    }
  }, []);

  useEffect(() => {
    // Fetch all data on component mount
    fetchSessions();
    fetchEmrTypes();
    fetchEmrFields();
    fetchClients();
    fetchEmrTypeResults();
    fetchManualFields();
  }, []);

  // Auto-hide success message after 2 seconds
  useEffect(() => {
    if (sessionSuccess) {
      const timer = setTimeout(() => {
        setSessionSuccess('');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [sessionSuccess]);

  const handleTypeChange = (emrTypeId) => {
    console.log('Type changed to:', emrTypeId);
    const selectedEmrType = emrTypes.find(type => type.id === emrTypeId);
    console.log('Selected EMR Type:', selectedEmrType);
    
    setSelectedType(emrTypeId);
    // Reset form when type changes - only keep instructions
    setSessionForm({
      instructions: ''
    });
  };

  const handleClientChange = (clientId) => {
    setSelectedClient(clientId);
  };

  const handleFormChange = (field, value) => {
    console.log(`handleFormChange called with field: ${field}, value: ${value}`);
    setSessionForm(prev => {
      const newState = {
        ...prev,
        [field]: value
      };
      console.log('New sessionForm state:', newState);
      return newState;
    });
  };

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingSession, setSavingSession] = useState(false);
  const [editError, setEditError] = useState('');

  // Handle Edit Session
  const handleEditSession = (session) => {
    console.log('=== EDIT SESSION DEBUG ===');
    console.log('Session data received:', session);
    console.log('session.instructions:', session.instructions);
    console.log('session.instructions type:', typeof session.instructions);
    console.log('session.manual_instructions:', session.manual_instructions);
    console.log('session.manual_instructions type:', typeof session.manual_instructions);
    console.log('session.client_id_name:', session.client_id_name);
    console.log('session.client field:', session.client);
    console.log('All session keys:', Object.keys(session));
    console.log('All session values:', Object.entries(session));
    console.log('Session emr_type_id:', session.emr_type_id);
    console.log('Session emr_name:', session.emr_name);
    console.log('Direct access test - session.dss_ba:', session.dss_ba);
    console.log('Direct access test - session.dss_name:', session.dss_name);
    console.log('Direct access test - session["dss_ba"]:', session["dss_ba"]);
    console.log('Direct access test - session["dss_name"]:', session["dss_name"]);
    setEditingSession(session);
    setEditError('');
    
    // Pre-fill the edit form with current session data
    const editFormData = {
      instructions: session.manual_instructions || ''
    };
    
    // Get all available fields for this EMR type (dynamic + manual)
    const availableFields = getAllFieldsForType(session.emr_type_id);
    
    console.log('Available fields for this EMR type:', availableFields);
    console.log('emrTypeResults for this EMR type:', emrTypeResults.filter(result => 
      result.emr_type_id === session.emr_type_id && result.status === "confirmed"
    ));
    console.log('Manual fields for this EMR type:', getManualFieldsForType(session.emr_type_id));
    
    // Create a mapping of possible field name variations
    const fieldNameMapping = {};
    availableFields.forEach(fieldName => {
      // Try different possible field name variations
      const variations = [
        fieldName, // Original field name (e.g., "appt date")
        fieldName.replace(/\s+/g, '_'), // Replace spaces with underscores (e.g., "appt_date")
        fieldName.replace(/\s+/g, ''), // Remove spaces (e.g., "apptdate")
        fieldName.toLowerCase().replace(/\s+/g, '_'), // Lowercase with underscores (e.g., "appt_date")
        fieldName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(''), // PascalCase (e.g., "ApptDate")
        fieldName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('_'), // PascalCase with underscores (e.g., "Appt_Date")
        // Additional variations for common field name patterns
        fieldName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''), // Clean snake_case
        fieldName.replace(/\s+/g, '_').toLowerCase(), // Simple space to underscore conversion
      ];
      
      console.log(`Trying variations for field "${fieldName}":`, variations);
      
      // Find which variation exists in the session data
      let foundValue = null;
      
      // Special handling for client_id field - look for client__id (double underscore) instead
      if (fieldName === 'client id' || fieldName === 'client_id') {
        console.log('=== CLIENT_ID FIELD DEBUG ===');
        console.log('session.hasOwnProperty("client__id"):', session.hasOwnProperty('client__id'));
        console.log('session.client__id value:', session.client__id);
        console.log('session.client__id type:', typeof session.client__id);
        console.log('session.client_id (UUID):', session.client_id);
        console.log('All session keys:', Object.keys(session));
        
        if (session.hasOwnProperty('client__id') && session.client__id !== null && session.client__id !== undefined && session.client__id !== '') {
          foundValue = session.client__id;
          console.log(`Found client_id field as "client__id" with value: "${foundValue}"`);
        } else {
          // If client__id is empty/null/undefined, don't show anything (not the UUID)
          foundValue = '';
          console.log(`client__id field is empty, setting to empty string`);
        }
        console.log('Final foundValue for client_id:', foundValue);
        console.log('=== END CLIENT_ID DEBUG ===');
      } else {
        // Normal field lookup for all other fields
        for (const variation of variations) {
          console.log(`Checking variation "${variation}" for field "${fieldName}"`);
          console.log(`session.hasOwnProperty("${variation}"):`, session.hasOwnProperty(variation));
          console.log(`session["${variation}"]:`, session[variation]);
          
          if (session.hasOwnProperty(variation) && session[variation] !== null && session[variation] !== undefined) {
            foundValue = session[variation];
            console.log(`Found field "${fieldName}" as "${variation}" with value: "${foundValue}"`);
            break;
          }
        }
      }
      
      // If not found with variations, try case-insensitive search through all session keys
      if (foundValue === null) {
        console.log(`Trying case-insensitive search for field "${fieldName}"`);
        const sessionKeys = Object.keys(session);
        for (const key of sessionKeys) {
          const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
          const normalizedFieldName = fieldName.toLowerCase().replace(/[^a-z0-9]/g, '');
          if (normalizedKey === normalizedFieldName) {
            foundValue = session[key];
            console.log(`Found field "${fieldName}" as "${key}" (case-insensitive match) with value: "${foundValue}"`);
            break;
          }
        }  
      }
      
      if (foundValue === null) {
        console.log(`Field "${fieldName}" not found in session data with any variation`);
      }
      
      // Get field type to set appropriate default value
      const fieldType = emrTypeResults.find(result => 
        result.emr_type_id === session.emr_type_id && 
        result.status === "confirmed" && 
        result.key === fieldName
      )?.type || 'text';
      
      // Set appropriate default value based on field type
      switch(fieldType) {
        case 'date':
          editFormData[fieldName] = foundValue || null;  // null for empty date
          break;
        case 'time':
          editFormData[fieldName] = foundValue || null;  // null for empty time
          break;
        case 'datetime':
          editFormData[fieldName] = foundValue || null;  // null for empty datetime
          break;
        case 'boolean':
          editFormData[fieldName] = foundValue !== null && foundValue !== undefined ? foundValue : false;  // false for empty boolean
          break;
        case 'text':
        case 'textarea':
        default:
          editFormData[fieldName] = foundValue || '';  // empty string for empty text
          break;
      }
    });
    
    // Only use fields from emrTypeResults to avoid duplicate field names
    // (UI display names vs API names)
    
    console.log('Edit form data being set:', editFormData);
    console.log('All fields in edit form:', Object.keys(editFormData));
    setEditForm(editFormData);
    setShowEditModal(true);
  };

  // Handle View Session Details
  const handleViewSessionDetails = (session) => {
    navigate(`/sessions/${session.id}`);
  };

  // Convert field name to API format (snake_case)
  const convertToApiFieldName = (fieldName) => {
    return fieldName.toLowerCase().replace(/\s+/g, '_');
  };

  // Handle Save Session Changes
  const handleSaveSessionChanges = async () => {
    if (!editingSession) return;
    
    setSavingSession(true);
    setEditError('');
    
    try {
      const updateData = {};
      
      // Add manual_instructions field (common for all types)
      updateData.manual_instructions = editForm.instructions || '';
      
      // Get field types from EMR field definitions first, then fallback to emrTypeResults or manual fields
      const fieldTypes = {};
      if (emrFields && Array.isArray(emrFields)) {
        // First try to get types from EMR field definitions
        emrFields.forEach(field => {
          fieldTypes[field.name] = field.type;
        });
      }
      // Add manual field types
      const typeManualFields = getManualFieldsForType(editingSession.emr_type_id);
      if (typeManualFields && Array.isArray(typeManualFields)) {
        typeManualFields.forEach(field => {
          if (!fieldTypes[field.name]) { // Only set if not already set by EMR field definitions
            fieldTypes[field.name] = field.type;
          }
        });
      }
      // Fallback to emrTypeResults for fields not found in EMR definitions or manual fields
      if (emrTypeResults && Array.isArray(emrTypeResults)) {
        const confirmedFields = emrTypeResults.filter(result => 
          result.emr_type_id === editingSession.emr_type_id && result.status === "confirmed"
        );
        confirmedFields.forEach(field => {
          if (!fieldTypes[field.key]) { // Only set if not already set by EMR field definitions or manual fields
            fieldTypes[field.key] = field.type;
          }
        });
      }
      
      // Add all dynamic fields from the edit form with proper type handling
      // Only send the original field names from emrTypeResults (UI display names)
      Object.keys(editForm).forEach(fieldName => {
        // Skip instructions field as it's handled separately above
        if (fieldName === 'instructions') return;
        
        const fieldType = fieldTypes[fieldName] || 'text';
        const fieldValue = editForm[fieldName];
        
        // Handle different field types according to backend requirements
        switch(fieldType) {
          case 'text':
            updateData[fieldName] = fieldValue || '';  // Empty string for empty text
            break;
          case 'date':
            if (fieldValue) {
              // Convert date string to ISO date format (YYYY-MM-DD)
              const date = new Date(fieldValue);
              // Check if the date is valid before calling toISOString()
              if (isNaN(date.getTime())) {
                console.warn(`Invalid date value for field ${fieldName}:`, fieldValue);
                updateData[fieldName] = null;
              } else {
                updateData[fieldName] = date.toISOString().split('T')[0];
              }
            } else {
              updateData[fieldName] = null;  // null for empty date
            }
            break;
          case 'time':
            updateData[fieldName] = fieldValue || null; // null for empty time
            break;
          case 'datetime':
            if (fieldValue) {
              // Convert datetime-local string to timestamp without timezone
              // Use the local time as-is without timezone conversion
              const date = new Date(fieldValue);
              if (isNaN(date.getTime())) {
                console.warn(`Invalid datetime value for field ${fieldName}:`, fieldValue);
                updateData[fieldName] = null;
              } else {
                // Format as YYYY-MM-DD HH:mm:ss without timezone conversion
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                const seconds = String(date.getSeconds()).padStart(2, '0');
                updateData[fieldName] = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
              }
            } else {
              updateData[fieldName] = null;  // null for empty date
            }
            break;
          case 'boolean':
            updateData[fieldName] = fieldValue ? 'true' : 'false';  // true/false for boolean
            break;
          case 'textarea':
            updateData[fieldName] = fieldValue || '';  // Empty string for empty textarea
            break;
          case 'dropdown':
            updateData[fieldName] = fieldValue || '';  // Empty string for empty dropdown
            break;
          default:
            updateData[fieldName] = fieldValue || '';
        }
      });

      console.log('=== SESSION UPDATE DEBUG ===');
      console.log('Edit form data:', editForm);
      console.log('Updating session with data:', updateData);
      console.log('Full updateData object:', JSON.stringify(updateData, null, 2));
      console.log('Dynamic fields check:');
      console.log('Fields being updated:', Object.keys(editForm));
      console.log('Original session client_id_name:', editingSession.client_id_name);
      console.log('Original session client field:', editingSession.client);
      console.log('Edit form client field:', editForm.client);
      console.log('API URL:', `${environment.apiUrl}/sessions/${editingSession.id}`);
      console.log('Session ID:', editingSession.id);
      console.log('Session ID type:', typeof editingSession.id);
      console.log('Session ID length:', editingSession.id ? editingSession.id.length : 'No ID');
      
      const requestBody = JSON.stringify(updateData);
      console.log('Request body being sent:', requestBody);
      console.log('Request headers:', {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      });
      console.log('Token being sent:', getToken());
      console.log('Token length:', getToken() ? getToken().length : 'No token');
      
      const response = await fetch(`${environment.apiUrl}/sessions/${editingSession.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: requestBody,
      });

      if (response.ok) {
        const updatedSession = await response.json();
        console.log('Session updated successfully:', updatedSession);
        console.log('Updated session client_id_name:', updatedSession.client_id_name);
        console.log('Updated session client field:', updatedSession.client);
        console.log('Updated session keys:', Object.keys(updatedSession));
        console.log('Updated session values:', Object.entries(updatedSession));
        console.log('Updated session dss_ba:', updatedSession.dss_ba);
        console.log('Updated session dss_name:', updatedSession.dss_name);
        
        // Refresh the sessions list to get the updated data
        await fetchSessions();
        
        // Close modal and show success
        setShowEditModal(false);
        setEditingSession(null);
        setEditForm({});
        setSessionSuccess('Session updated successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('=== UPDATE ERROR DEBUG ===');
        console.error('Response status:', response.status);
        console.error('Response status text:', response.statusText);
        console.error('Full error response:', errorData);
        console.error('Error detail:', errorData.detail);
        console.error('Error detail type:', typeof errorData.detail);
        
        // Enhanced error handling for different error types
        let errorMessage = 'Failed to update session';
        
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Handle validation errors array
            console.error('Validation errors:');
            errorData.detail.forEach((error, index) => {
              console.error(`Error ${index + 1}:`, error);
              console.error(`  Type: ${error.type}`);
              console.error(`  Location: ${error.loc}`);
              console.error(`  Message: ${error.msg}`);
              console.error(`  Input: ${error.input}`);
            });
            errorMessage = errorData.detail.map(err => err.msg || err.message || 'Validation error').join(', ');
          } else if (typeof errorData.detail === 'object') {
            errorMessage = errorData.detail.msg || errorData.detail.message || 'Validation error';
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
        
        // Special handling for field already exists error
        if (errorMessage.includes("already exists") || errorMessage.includes("field with name")) {
          errorMessage = `Field Error: ${errorMessage}`;
        }
        
        console.error('========================');
        setEditError(errorMessage);
      }
    } catch (err) {
      console.error('Network error updating session:', err);
      setEditError('Network error updating session');
    } finally {
      setSavingSession(false);
    }
  };

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [deletingSession, setDeletingSession] = useState(false);
  


  // Handle Delete Session
  const handleDeleteSession = (session) => {
    setSessionToDelete(session);
    setShowDeleteModal(true);
  };

  // Confirm Delete Session
  const confirmDeleteSession = async () => {
    if (!sessionToDelete) return;
    
    setDeletingSession(true);
    try {
      const response = await fetch(`${environment.apiUrl}/sessions/${sessionToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        setSessionSuccess('Session deleted successfully!');
        setShowDeleteModal(false);
        setSessionToDelete(null);
        // Refresh the sessions list
        await fetchSessions();
      } else {
        setSessionError('Failed to delete session');
      }
    } catch (err) {
      setSessionError('Network error deleting session');
    } finally {
      setDeletingSession(false);
    }
  };

  const handleCreateSession = async () => {
    console.log('=== FUNCTION CALLED ===');
    console.log('handleCreateSession function was called!');
    console.log('========================');
    
    // Debug: Log the current form state
    console.log('=== FORM STATE DEBUG ===');
    console.log('Current sessionForm:', sessionForm);
    console.log('sessionForm.instructions:', sessionForm.instructions);
    console.log('sessionForm.instructions type:', typeof sessionForm.instructions);
    console.log('========================');
    
    // Validate required fields
    if (!isFormValid()) {
      console.log('=== VALIDATION FAILED ===');
      console.log('isFormValid() returned false');
      console.log('selectedType:', selectedType);
      console.log('selectedClient:', selectedClient);
      console.log('========================');
      setSessionError('Please select both Type and Client before creating a session.');
      return;
    }

    setCreatingSession(true);
    setSessionError('');
    setSessionSuccess('');

    try {
      const sessionData = {
        emr_type_id: selectedType,
        client_id: selectedClient,
      };

      // Add manual_instructions field (common for all types)
      console.log('=== MANUAL_INSTRUCTIONS DEBUG ===');
      console.log('sessionForm.instructions:', sessionForm.instructions);
      console.log('sessionForm.instructions type:', typeof sessionForm.instructions);
      console.log('sessionForm.instructions length:', sessionForm.instructions ? sessionForm.instructions.length : 'undefined');
      sessionData.manual_instructions = sessionForm.instructions || '';
      console.log('sessionData.manual_instructions after assignment:', sessionData.manual_instructions);
      console.log('========================');

      // Add all fields (dynamic + manual) based on EMR type with proper type handling
      // Send ALL fields even if they don't exist in sessionForm
      const allFields = getAllFieldsForType(selectedType);
      if (allFields.length > 0) {
        allFields.forEach(fieldName => {
          // Get field type from EMR field definitions first, then fallback to manual fields or emrTypeResults
          let fieldType = 'text';
          
          // First try to find in EMR field definitions
          const emrField = emrFields.find(field => 
            field.name.toLowerCase() === fieldName.toLowerCase()
          );
          if (emrField) {
            fieldType = emrField.type;
          } else {
            // Try to find in manual fields
            const manualField = getManualFieldsForType(selectedType).find(field => 
              field.name.toLowerCase() === fieldName.toLowerCase()
            );
            if (manualField) {
              fieldType = manualField.type;
            } else {
              // Fallback to emrTypeResults
              fieldType = emrTypeResults.find(result => 
                result.emr_type_id === selectedType && 
                result.status === "confirmed" && 
                result.key === fieldName
              )?.type || 'text';
            }
          }
          
          // Always send the field, even if empty
          const fieldValue = sessionForm[fieldName];
          
          // Handle different field types according to backend requirements
          switch(fieldType) {
            case 'text':
              sessionData[fieldName] = fieldValue || '';  // Empty string for empty text
              break;
            case 'date':
              if (fieldValue) {
                // Convert date string to ISO date format (YYYY-MM-DD)
                const date = new Date(fieldValue);
                // Check if the date is valid before calling toISOString()
                if (isNaN(date.getTime())) {
                  console.warn(`Invalid date value for field ${fieldName}:`, fieldValue);
                  sessionData[fieldName] = null;
                } else {
                  sessionData[fieldName] = date.toISOString().split('T')[0];
                }
              } else {
                sessionData[fieldName] = null;  // null for empty date
              }
              break;
            case 'time':
              sessionData[fieldName] = fieldValue || null; // null for empty time
              break;
            case 'datetime':
              if (fieldValue) {
                // Convert datetime-local string to timestamp without timezone
                // Use the local time as-is without timezone conversion
                const date = new Date(fieldValue);
                if (isNaN(date.getTime())) {
                  console.warn(`Invalid datetime value for field ${fieldName}:`, fieldValue);
                  sessionData[fieldName] = null;
                } else {
                  // Format as YYYY-MM-DD HH:mm:ss without timezone conversion
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  const seconds = String(date.getSeconds()).padStart(2, '0');
                  sessionData[fieldName] = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                }
              } else {
                sessionData[fieldName] = null;  // null for empty date
              }
              break;
            case 'boolean':
              sessionData[fieldName] = fieldValue ? 'true' : 'false';  // true/false for boolean
              break;
            case 'textarea':
              sessionData[fieldName] = fieldValue || '';  // Empty string for empty textarea
              break;
            case 'dropdown':
              sessionData[fieldName] = fieldValue || '';  // Empty string for empty dropdown
              break;
            default:
              sessionData[fieldName] = fieldValue || '';
          }
        });
      }

      // Debug logging - log what we're about to send
      console.log('=== SESSION CREATION DEBUG ===');
      console.log('environment.apiUrl:', environment.apiUrl);
      console.log('selectedType:', selectedType, 'type:', typeof selectedType);
      console.log('selectedClient:', selectedClient, 'type:', typeof selectedClient);

      console.log('sessionForm:', sessionForm);
      console.log('Final sessionData to send:', sessionData);
      console.log('API URL:', `${environment.apiUrl}/sessions`);
      console.log('Full URL being called:', `${environment.apiUrl}/sessions`);
      
      // Validate data types before sending
      if (!selectedType || selectedType === '') {
        console.error('ERROR: selectedType is empty or invalid');
        setSessionError('Please select a valid EMR Type');
        setCreatingSession(false);
        return;
      }
      
      if (!selectedClient || selectedClient === '') {
        console.error('ERROR: selectedClient is empty or invalid');
        setSessionError('Please select a valid Client');
        setCreatingSession(false);
        return;
      }
      
      // Ensure UUIDs are valid format (basic check)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(selectedType)) {
        console.error('ERROR: selectedType is not a valid UUID:', selectedType);
        setSessionError('Invalid EMR Type format');
        setCreatingSession(false);
        return;
      }
      
      if (!uuidRegex.test(selectedClient)) {
        console.error('ERROR: selectedClient is not a valid UUID:', selectedClient);
        setSessionError('Invalid Client format');
        setCreatingSession(false);
        return;
      }
      
      // Log the expected schema format for debugging
      console.log('Expected schema format:');
      console.log('- emr_type_id: UUID (required)');
      console.log('- client_id: UUID (required)');
      console.log('- manual_instructions: string (optional)');
      console.log('- Dynamic fields: Based on confirmed EMR type results');
      
      console.log('================================');

      const response = await fetch(`${environment.apiUrl}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(sessionData),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('=== SUCCESS RESPONSE ===');
        console.log('Response data:', data);
        console.log('Created session type:', data.emr_type_name || 'No type name in response');
        console.log('Created session type ID:', data.emr_type_id);
        console.log('Fields returned from backend:', Object.keys(data));
        console.log('Dynamic fields that were sent:', Object.keys(sessionData).filter(key => !['emr_type_id', 'client_id', 'manual_instructions'].includes(key)));
        
        // Check if dynamic fields are missing from the response
        const sentDynamicFields = Object.keys(sessionData).filter(key => !['emr_type_id', 'client_id', 'manual_instructions'].includes(key));
        const returnedDynamicFields = Object.keys(data).filter(key => !['id', 'emr_type_id', 'client_id', 'user_id', 'emr_name', 'client_id_name', 'created_at', 'updated_at', 'manual_instructions'].includes(key));
        
        console.log('=== DYNAMIC FIELDS COMPARISON ===');
        console.log('Dynamic fields sent to backend:', sentDynamicFields);
        console.log('Dynamic fields returned from backend:', returnedDynamicFields);
        console.log('Missing fields:', sentDynamicFields.filter(field => !returnedDynamicFields.includes(field)));
        console.log('Extra fields returned:', returnedDynamicFields.filter(field => !sentDynamicFields.includes(field)));
        console.log('========================');
        
        setSessionSuccess('Session created successfully!');
        setShowNewSession(false);
        setSelectedType('');
        setSelectedClient('');
        setSessionForm({
          instructions: ''
        });
        
        // Refresh sessions and update tabs
        await fetchSessions();
        
        // Set the newly created session's type as the active tab
        const selectedEmrType = emrTypes.find(type => type.id === selectedType);
        if (selectedEmrType) {
          setActiveTab(selectedEmrType.name);
        }
        
        // Redirect to the newly created session
        if (data && data.id) {
          navigate(`/sessions/${data.id}`, {
            state: { isNewSession: true }
          });
        }
      } else {
        console.log('=== ERROR RESPONSE ===');
        console.log('Response status:', response.status);
        console.log('Response statusText:', response.statusText);
        const errorData = await response.json().catch(() => ({}));
        console.log('Error response data:', errorData);
        console.log('====================');
        
        // Enhanced error handling for different error types
        let errorMessage = 'Failed to create session';
        
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Handle validation errors array
            errorMessage = errorData.detail.map(err => err.msg || err.message || 'Validation error').join(', ');
          } else if (typeof errorData.detail === 'object') {
            errorMessage = errorData.detail.msg || errorData.detail.message || 'Validation error';
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
        
        // Special handling for field already exists error
        if (errorMessage.includes("already exists") || errorMessage.includes("field with name")) {
          errorMessage = `Field Error: ${errorMessage}`;
        }
        
        setSessionError(errorMessage);
      }
    } catch (err) {
      setSessionError('Network error creating session');
    } finally {
      setCreatingSession(false);
    }
  };

  // Check if form is valid (both dropdowns have values)
  const isFormValid = () => {
    console.log('=== FORM VALIDATION DEBUG ===');
    console.log('selectedType:', selectedType, 'type:', typeof selectedType);
    console.log('selectedClient:', selectedClient, 'type:', typeof selectedClient);
    console.log('selectedType truthy:', !!selectedType);
    console.log('selectedClient truthy:', !!selectedClient);
    const isValid = selectedType && selectedClient;
    console.log('Form is valid:', isValid);
    console.log('========================');
    return isValid;
  };



  // Get sessions for a specific type
  const getSessionsByType = (typeName) => {
    console.log('=== SESSION FILTERING DEBUG ===');
    console.log('Looking for sessions of type:', typeName);
    console.log('All sessions:', sessions);
    console.log('Active tab:', activeTab);
    
    const filteredSessions = sessions.filter(session => {
      console.log('Checking session:', session);
      console.log('Session emr_name:', session.emr_name, typeof session.emr_name);
      console.log('Session emr_type_id:', session.emr_type_id);
      console.log('Comparing with typeName:', typeName);
      console.log('Match?', session.emr_name === typeName);
      return session.emr_name === typeName;
    });
    
    console.log('Filtered sessions for type:', typeName, filteredSessions);
    console.log('================================');
    return filteredSessions;
  };

  // Safe render helper function
  const safeRender = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value.toString();
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') {
      console.warn('Attempted to render object:', value);
      // Check if it's a validation error object
      if (value.msg) return value.msg;
      if (value.message) return value.message;
      if (value.error) return value.error;
      return fallback;
    }
    return fallback;
  };

  // Helper function to render field based on type
  const renderFieldByType = (fieldName, fieldValue, fieldType, onChangeHandler = null) => {
    // Use provided onChangeHandler or default to setEditForm
    const handleChange = onChangeHandler || ((e) => setEditForm(prev => ({ ...prev, [fieldName]: e.target.value })));
    const handleCheckboxChange = onChangeHandler || ((e) => setEditForm(prev => ({ ...prev, [fieldName]: e.target.checked })));
    
    switch(fieldType) {
      case 'date':
        return (
          <input
            type="date"
            value={fieldValue || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );
      case 'time':
        return (
          <input
            type="time"
            value={fieldValue || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );
      case 'datetime':
        return (
          <input
            type="datetime-local"
            value={fieldValue || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );
      case 'boolean':
        const booleanValue = typeof fieldValue === 'string' 
          ? fieldValue.toLowerCase() === 'true'
          : Boolean(fieldValue);
        return (
          <input
            type="checkbox"
            id={`field_${fieldName}`}
            checked={booleanValue}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
        );
      case 'textarea':
        return (
          <textarea
            value={fieldValue || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="2"
            placeholder={`Enter ${fieldName.toLowerCase()}`}
          />
        );
      case 'dropdown':
        // Get dropdown values from EMR field definitions
        const dropdownField = emrFields.find(field => 
          field.name.toLowerCase() === fieldName.toLowerCase() && 
          field.type === 'dropdown'
        );
        const dropdownOptions = dropdownField?.dropdown_values?.split('\n').filter(val => val.trim()) || [];
        
        return (
          <select
            value={fieldValue || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select an option</option>
            {dropdownOptions.map((option, index) => (
              <option key={index} value={option.trim()}>
                {option.trim()}
              </option>
            ))}
          </select>
        );
      case 'text':
      default:
        return (
          <input
            type="text"
            value={fieldValue || ''}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={`Enter ${fieldName.toLowerCase()}`}
          />
        );
    }
  };

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      let date;
      // Check if it's a date-only string (YYYY-MM-DD) or datetime string
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Date-only string like "2025-06-01" - add time to avoid timezone issues
        date = new Date(dateString + 'T00:00:00');
      } else {
        // DateTime string like "2025-06-27 15:41:55" - parse directly
        date = new Date(dateString);
      }
      
      if (isNaN(date)) return dateString;
      
      const formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      // Remove the word "at" from the formatted date string
      return formattedDate.replace(' at ', ' ');
    } catch (error) {
      return dateString;
    }
  };

  // Safe string helper for tab names
  const safeString = (value) => {
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      console.warn('Tab name is an object:', value);
      return 'Unknown Type';
    }
    return 'Unknown Type';
  };

  // Get dynamic fields based on selected EMR type and confirmed results
  const getDynamicFields = () => {
    console.log('=== GET DYNAMIC FIELDS DEBUG ===');
    console.log('selectedType:', selectedType, 'type:', typeof selectedType);
    console.log('emrTypeResults:', emrTypeResults);
    console.log('emrTypeResults is array:', Array.isArray(emrTypeResults));
    console.log('emrTypeResults length:', emrTypeResults?.length);
    
    if (!selectedType || !emrTypeResults || !Array.isArray(emrTypeResults)) {
      console.log('Early return - missing data');
      console.log('========================');
      return [];
    }
    
    // Log all results to see what we have
    console.log('All emrTypeResults:', emrTypeResults);
    
    // Filter results by emr_type_id AND status = "confirmed"
    const confirmedFields = emrTypeResults.filter(result => {
      console.log('Checking result:', result);
      console.log('result.emr_type_id:', result.emr_type_id, 'type:', typeof result.emr_type_id);
      console.log('selectedType:', selectedType, 'type:', typeof selectedType);
      console.log('result.status:', result.status);
      const matches = result.emr_type_id === selectedType && result.status === "confirmed";
      console.log('Match?', matches);
      return matches;
    });
    
    console.log('Confirmed fields found:', confirmedFields);
    
    // Return unique field names
    const uniqueFields = [...new Set(confirmedFields.map(result => result.key))];
    console.log('Unique field names:', uniqueFields);
    console.log('========================');
    return uniqueFields;
  };

  // Get all fields (dynamic + manual) for a specific EMR type
  const getAllFieldsForType = (emrTypeId) => {
    const dynamicFields = [];
    const manualFields = [];
    
    // Get dynamic fields from emrTypeResults
    if (emrTypeResults && Array.isArray(emrTypeResults)) {
      const confirmedFields = emrTypeResults.filter(result => 
        result.emr_type_id === emrTypeId && result.status === "confirmed"
      );
      dynamicFields.push(...confirmedFields.map(result => result.key));
    }
    
    // Get manual fields
    const typeManualFields = getManualFieldsForType(emrTypeId);
    if (typeManualFields.length > 0) {
      manualFields.push(...typeManualFields.map(field => field.name));
    }
    
    // Combine and return unique fields
    const allFields = [...dynamicFields, ...manualFields];
    const uniqueFields = [...new Set(allFields)];
    
    console.log('All fields for EMR type:', emrTypeId, {
      dynamicFields,
      manualFields,
      allFields: uniqueFields
    });
    
    return uniqueFields;
  };

  // Get manual fields for a specific EMR type
  const getManualFieldsForType = (emrTypeId) => {
    if (!emrTypeId || !manualFields || !Array.isArray(manualFields)) {
      return [];
    }
    
    // Filter manual fields by emr_type_id
    const typeManualFields = manualFields.filter(field => 
      field.emr_type_id === emrTypeId
    );
    
    console.log('Manual fields for EMR type:', emrTypeId, typeManualFields);
    return typeManualFields;
  };

  // Format field name for display (e.g., "client id" -> "Client Id")
  const formatFieldName = (fieldName) => {
    return fieldName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Fields to Display functionality
  const openFieldsModal = () => {
    if (!activeTab) return;
    
    // Get the EMR type ID for the active tab
    const activeEmrType = emrTypes.find(type => type.name === activeTab);
    if (!activeEmrType) {
      console.warn('EMR type not found for active tab:', activeTab);
      return;
    }
    
    // Get all available fields for this EMR type (dynamic + manual)
    const allFields = getAllFieldsForType(activeEmrType.id);
    
    // Add common fields that are always available
    const commonFields = ['Type', 'Client', 'Instructions', 'Created At'];
    allFields.push(...commonFields);
    
    // Get current visible fields from config or use defaults
    const currentConfig = fieldDisplayConfig[activeTab] || ['Type', 'Client', 'Instructions', 'Created At'];
    
    // Set available fields (all fields minus visible fields)
    const available = allFields.filter(field => !currentConfig.includes(field));
    
    console.log('=== FIELDS MODAL DEBUG ===');
    console.log('Active tab:', activeTab);
    console.log('Active EMR type:', activeEmrType);
    console.log('All fields:', allFields);
    console.log('Current config:', currentConfig);
    console.log('Available fields:', available);
    console.log('========================');
    
    setAvailableFields(available);
    setVisibleFields(currentConfig);
    setSelectedAvailableField(null);
    setSelectedVisibleField(null);
    setShowFieldsModal(true);
  };

  const moveFieldToVisible = () => {
    if (!selectedAvailableField) return;
    
    setVisibleFields(prev => [...prev, selectedAvailableField]);
    setAvailableFields(prev => prev.filter(field => field !== selectedAvailableField));
    setSelectedAvailableField(null);
  };

  const moveFieldToAvailable = () => {
    if (!selectedVisibleField) return;
    
    setAvailableFields(prev => [...prev, selectedVisibleField]);
    setVisibleFields(prev => prev.filter(field => field !== selectedVisibleField));
    setSelectedVisibleField(null);
  };

  const moveFieldUp = () => {
    if (!selectedVisibleField) return;
    
    const currentIndex = visibleFields.indexOf(selectedVisibleField);
    if (currentIndex <= 0) return;
    
    const newVisibleFields = [...visibleFields];
    [newVisibleFields[currentIndex], newVisibleFields[currentIndex - 1]] = 
    [newVisibleFields[currentIndex - 1], newVisibleFields[currentIndex]];
    
    setVisibleFields(newVisibleFields);
  };

  const moveFieldDown = () => {
    if (!selectedVisibleField) return;
    
    const currentIndex = visibleFields.indexOf(selectedVisibleField);
    if (currentIndex >= visibleFields.length - 1) return;
    
    const newVisibleFields = [...visibleFields];
    [newVisibleFields[currentIndex], newVisibleFields[currentIndex + 1]] = 
    [newVisibleFields[currentIndex + 1], newVisibleFields[currentIndex]];
    
    setVisibleFields(newVisibleFields);
  };

  const saveFieldDisplayConfig = () => {
    const newConfig = {
      ...fieldDisplayConfig,
      [activeTab]: visibleFields
    };
    setFieldDisplayConfig(newConfig);
    
    // Save to localStorage
    localStorage.setItem('fieldDisplayConfig', JSON.stringify(newConfig));
    
    setShowFieldsModal(false);
  };

  const getVisibleFieldsForCurrentTab = () => {
    return fieldDisplayConfig[activeTab] || ['Type', 'Client', 'Instructions', 'Created At'];
  };

  const renderFieldValueForTable = (session, fieldName) => {
    const value = renderFieldValue(session, fieldName, formatDate);
    
    switch (fieldName) {
      case 'Type':
        return (
          <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-md text-xs font-semibold">
            {safeRender(value)}
          </span>
        );
      default:
        return safeRender(value);
    }
  };



  if (loading) {
    return (
      <div className="w-full flex flex-col items-center justify-center">
        <div className="text-lg text-slate-700">Loading sessions...</div>
      </div>
    );
  }

  // Debug logging with safety checks
  // Debug logging with safety checks
  try {
    console.log('=== COMPONENT RENDER DEBUG ===');
    console.log('Component render - sessions:', sessions);
    console.log('Component render - sessions length:', sessions?.length);
    console.log('Component render - sessions is array:', Array.isArray(sessions));
    console.log('Component render - emrTypes:', emrTypes);
    console.log('Component render - clients:', clients);
    console.log('Component render - sessionTabs:', sessionTabs);
    console.log('Component render - activeTab:', activeTab);
    console.log('================================');
  } catch (debugError) {
    console.error('Error in debug logging:', debugError);
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-700">Sessions</h2>
        <button
          onClick={() => setShowNewSession(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          New Session
        </button>
      </div>

      {/* Success/Error Messages */}
      {sessionSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{sessionSuccess}</p>
            </div>
          </div>
        </div>
      )}
      {sessionError && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{typeof sessionError === 'string' ? sessionError : sessionError?.msg || sessionError?.message || JSON.stringify(sessionError)}</p>
            </div>
          </div>
        </div>
      )}

      {/* New Session Modal */}
      {showNewSession && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-30 pt-8">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] m-4">
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 4rem)' }}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-slate-700">Create New Session</h3>
                <button
                  onClick={() => {
                    setShowNewSession(false);
                    setSelectedType('');
                    setSelectedClient('');
                    setSessionForm({
                      instructions: ''
                    });
                  }}
                  className="text-slate-400 hover:text-slate-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              
              {/* Type Dropdown */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Type *
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loadingEmrTypes}
                  required
                >
                  <option value="">
                    {loadingEmrTypes ? 'Loading EMR types...' : 'Select an EMR type'}
                  </option>
                  {emrTypes && Array.isArray(emrTypes) && emrTypes.map((type) => (
                    <option key={type?.id || 'unknown'} value={type?.id || ''}>
                      {safeString(type?.name)}
                    </option>
                  ))}
                </select>
                {emrTypesError && (
                  <p className="text-red-600 text-sm mt-1">{typeof emrTypesError === 'string' ? emrTypesError : emrTypesError?.msg || emrTypesError?.message || JSON.stringify(emrTypesError)}</p>
                )}
              </div>

              {/* Client Dropdown */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Client *
                </label>
                <select
                  value={selectedClient}
                  onChange={(e) => handleClientChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loadingClients}
                  required
                >
                  <option value="">
                    {loadingClients ? 'Loading clients...' : 'Select a client'}
                  </option>
                  {clients && Array.isArray(clients) && clients.map((client) => (
                    <option key={client?.id || 'unknown'} value={client?.id || ''}>
                      {safeString(client?.first_name)} {safeString(client?.last_name)}
                    </option>
                  ))}
                </select>
                {clientsError && (
                  <p className="text-red-600 text-sm mt-1">{typeof clientsError === 'string' ? clientsError : clientsError?.msg || clientsError?.message || JSON.stringify(clientsError)}</p>
                )}
              </div>

              {/* All Fields based on EMR Type (Dynamic + Manual) */}
              {selectedType && (
                <div className="space-y-4 border-t pt-4">
                  {/* Manual Fields Error Display */}
                  {manualFieldsError && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium">Manual Fields Error: {manualFieldsError}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {(() => {
                    const allFields = getAllFieldsForType(selectedType);
                    
                    if (allFields.length === 0) {
                      return (
                        <div className="text-center py-12">
                          <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                            <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 shadow-sm">
                            <h3 className="text-xl font-bold text-amber-900 mb-3">No fields available</h3>
                            <p className="text-amber-700 text-base leading-relaxed">
                              You don't have any confirmed fields or manual fields configured for this EMR type yet.
                              <br />
                              <span className="text-sm text-amber-600 mt-2 block">
                                Fields will appear here once they are confirmed in the EMR type analysis or configured as manual fields.
                              </span>
                            </p>
                          </div>
                        </div>
                      );
                    }
                    
                    return allFields.map((fieldName, index) => {
                      // Get field type from EMR field definitions first, then fallback to manual fields or emrTypeResults
                      let fieldType = 'text';
                      
                      // First try to find in EMR field definitions
                      const emrField = emrFields.find(field => 
                        field.name.toLowerCase() === fieldName.toLowerCase()
                      );
                      if (emrField) {
                        fieldType = emrField.type;
                      } else {
                        // Try to find in manual fields
                        const manualField = getManualFieldsForType(selectedType).find(field => 
                          field.name.toLowerCase() === fieldName.toLowerCase()
                        );
                        if (manualField) {
                          fieldType = manualField.type;
                        } else {
                          // Fallback to emrTypeResults
                          fieldType = emrTypeResults.find(result => 
                            result.emr_type_id === selectedType && 
                            result.status === "confirmed" && 
                            result.key === fieldName
                          )?.type || 'text';
                        }
                      }
                      
                      return (
                        <div key={index}>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {formatFieldName(fieldName)}
                          </label>
                          {renderFieldByType(fieldName, sessionForm[fieldName], fieldType, (e) => {
                            const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
                            handleFormChange(fieldName, value);
                          })}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}

              {/* Instructions Field */}
              <div className="mt-6 mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Instructions
                </label>
                <textarea
                  value={sessionForm.instructions}
                  onChange={(e) => handleFormChange('instructions', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Enter session instructions"
                />
              </div>






              
              <div className="flex gap-3 pt-6 border-t">
                <button
                  onClick={() => {
                    console.log('=== BUTTON CLICKED ===');
                    console.log('Create Session button was clicked!');
                    console.log('Form validation result:', isFormValid());
                    console.log('========================');
                    handleCreateSession();
                  }}
                  disabled={!isFormValid() || creatingSession}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    isFormValid() && !creatingSession
                      ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer' 
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                >
                  {creatingSession ? 'Creating...' : 'Create Session'}
                </button>
                <button
                  onClick={() => {
                    setShowNewSession(false);
                    setSelectedType('');
                    setSelectedClient('');
                    setSessionForm({
                      instructions: ''
                    });
                  }}
                  className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sessions Tabs */}
      {sessionTabs.length > 0 && sessions && Array.isArray(sessions) ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-slate-200 bg-slate-50">
            <div className="flex space-x-1 px-6">
              {sessionTabs.map((tabName) => (
                <button
                  key={tabName}
                  onClick={() => setActiveTab(tabName)}
                  className={`px-6 py-4 text-sm font-medium rounded-t-lg transition-all duration-200 ${
                    activeTab === tabName
                      ? 'bg-white text-blue-700 border-b-2 border-blue-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                  }`}
                >
                  {tabName}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-slate-800">{activeTab} Sessions</h3>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                      {getSessionsByType(activeTab).length} session{getSessionsByType(activeTab).length !== 1 ? 's' : ''}
                    </span>
                    <button
                      onClick={openFieldsModal}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-200 group relative"
                      title="Fields to Display"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                        Fields to Display
                      </span>
                    </button>
                  </div>
                </div>
                {getSessionsByType(activeTab).length === 0 ? (
                  <div className="text-center py-12">
                    <div className="mx-auto h-12 w-12 text-slate-400 mb-4">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No {activeTab} sessions found</h3>
                    <p className="text-slate-500">Create your first {activeTab} session using the "New Session" button above.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                        <tr>
                          {getVisibleFieldsForCurrentTab().map((fieldName) => (
                            <th key={fieldName} className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                              {fieldName}
                            </th>
                          ))}
                          <th className="px-6 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {getSessionsByType(activeTab).map((session, index) => (
                          <tr 
                            key={session.id} 
                            className={`hover:bg-blue-100 transition-colors duration-150 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}
                            onClick={() => handleViewSessionDetails(session)}
                          >
                            {getVisibleFieldsForCurrentTab().map((fieldName) => (
                              <td key={fieldName} className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                                {renderFieldValueForTable(session, fieldName)}
                              </td>
                            ))}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          <div className="flex gap-2">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('=== EDIT BUTTON CLICKED ===');
                                    console.log('Session being edited:', session);
                                    console.log('Session ID:', session.id);
                                    console.log('Session emr_type_id:', session.emr_type_id);
                                    console.log('Session emr_name:', session.emr_name);
                                    console.log('All session keys:', Object.keys(session));
                                    console.log('All session values:', Object.entries(session));
                                    handleEditSession(session);
                                  }}
                                  className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                  title="Edit Session"
                                >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSession(session);
                                  }}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                  title="Delete Session"
                                >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <div className="text-center text-slate-500 py-8">
            No sessions found. Create your first session using the "New Session" button above.
          </div>
        </div>
      )}

      {/* Edit Session Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 pt-8">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] m-4 overflow-hidden">
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 4rem)' }}>
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-slate-700">Edit Session</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingSession(null);
                    setEditForm({});
                  }}
                  className="text-slate-400 hover:text-slate-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Session Info - Only show if there are confirmed fields */}
              {(() => {
                const availableFields = [];
                if (emrTypeResults && Array.isArray(emrTypeResults)) {
                  const confirmedFields = emrTypeResults.filter(result => 
                    result.emr_type_id === editingSession.emr_type_id && result.status === "confirmed"
                  );
                  const fieldNames = confirmedFields.map(result => result.key);
                  const uniqueFieldNames = fieldNames.filter((fieldName, index, self) => 
                    self.findIndex(f => f.toLowerCase() === fieldName.toLowerCase()) === index
                  );
                  availableFields.push(...uniqueFieldNames);
                }
                
                // Only show Session Info if there are confirmed fields
                if (availableFields.length === 0) {
                  return null;
                }
                
                return (
                  <div className="mb-6">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                      <h4 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Session Information
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-blue-100">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">Type</span>
                            <p className="text-sm font-semibold text-slate-900">{editingSession?.emr_name}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-blue-100">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-green-600 uppercase tracking-wide">Client</span>
                            <p className="text-sm font-semibold text-slate-900">{editingSession?.client_id_name}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-white/70 rounded-lg border border-blue-100">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-purple-600 uppercase tracking-wide">Created</span>
                            <p className="text-sm font-semibold text-slate-900">
                              {editingSession?.created_at ? (() => {
                                try {
                                  const date = new Date(editingSession.created_at);
                                  if (isNaN(date.getTime())) {
                                    return 'Invalid date';
                                  }
                                  const formattedDate = date.toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  });
                                  return formattedDate.replace(' at ', ' ');
                                } catch (error) {
                                  return 'Invalid date';
                                }
                              })() : '—'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Edit Form */}
              <div className="space-y-4">
                {/* Dynamic Fields - Show all available fields for this EMR type */}
                {editingSession && (
                  <div className="space-y-4 border-t pt-4">
                    <h4 className="text-lg font-medium text-slate-700">Session Fields</h4>
                    {/* Get all available fields for this EMR type */}
                    {(() => {
                      const availableFields = getAllFieldsForType(editingSession.emr_type_id);
                      
                      console.log('Available fields for this EMR type in edit modal:', availableFields);
                      
                      // Show message if no confirmed fields available
                      if (availableFields.length === 0) {
                        return (
                          <div className="text-center py-12">
                            <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                              <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 shadow-sm">
                              <h3 className="text-xl font-bold text-amber-900 mb-3">No confirmed fields available</h3>
                              <p className="text-amber-700 text-base leading-relaxed">
                                You don't have any confirmed fields in the results table yet. 
                                <br />
                                <span className="text-sm text-amber-600 mt-2 block">
                                  Fields will appear here once they are confirmed in the EMR type analysis.
                                </span>
                              </p>
                            </div>
                          </div>
                        );
                      }
                      
                      return availableFields.map((fieldName) => {
                        // Get field type from EMR field definitions first, then fallback to manual fields or emrTypeResults
                        let fieldType = 'text';
                        
                        // First try to find in EMR field definitions
                        const emrField = emrFields.find(field => 
                          field.name.toLowerCase() === fieldName.toLowerCase()
                        );
                        if (emrField) {
                          fieldType = emrField.type;
                        } else {
                          // Try to find in manual fields
                          const manualField = getManualFieldsForType(editingSession.emr_type_id).find(field => 
                            field.name.toLowerCase() === fieldName.toLowerCase()
                          );
                          if (manualField) {
                            fieldType = manualField.type;
                          } else {
                            // Fallback to emrTypeResults
                            fieldType = emrTypeResults.find(result => 
                              result.emr_type_id === editingSession.emr_type_id && 
                              result.status === "confirmed" && 
                              result.key === fieldName
                            )?.type || 'text';
                          }
                        }
                        
                        // Skip client field in edit mode since it can't be changed
                        if (fieldName === 'client') {
                          return null;
                        }
                        
                        // Handle client_id field specially - ensure it shows custom data, not UUID
                        if (fieldName === 'client id' || fieldName === 'client_id') {
                          // Get the value from editForm (which should already be handled by our special logic)
                          const fieldValue = editForm[fieldName] || '';
                          return (
                            <div key={fieldName}>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                {fieldName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                              </label>
                              <input
                                type="text"
                                value={fieldValue}
                                onChange={(e) => setEditForm(prev => ({ ...prev, [fieldName]: e.target.value }))}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder={`Enter ${fieldName.toLowerCase()}`}
                              />
                            </div>
                          );
                        }
                        

                        
                        // Use the renderFieldByType function for all other fields
                        return (
                          <div key={fieldName}>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              {fieldName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </label>
                            {renderFieldByType(fieldName, editForm[fieldName], fieldType)}
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}

                {/* Instructions Field - Only show if there are confirmed fields */}
                {(() => {
                  const availableFields = [];
                  if (emrTypeResults && Array.isArray(emrTypeResults)) {
                    const confirmedFields = emrTypeResults.filter(result => 
                      result.emr_type_id === editingSession.emr_type_id && result.status === "confirmed"
                    );
                    const fieldNames = confirmedFields.map(result => result.key);
                    const uniqueFieldNames = fieldNames.filter((fieldName, index, self) => 
                      self.findIndex(f => f.toLowerCase() === fieldName.toLowerCase()) === index
                    );
                    availableFields.push(...uniqueFieldNames);
                  }
                  
                  // Only show Instructions if there are confirmed fields
                  if (availableFields.length === 0) {
                    return null;
                  }
                  
                  return (
                    <div className="space-y-4 border-t pt-4">
                      <h4 className="text-lg font-medium text-slate-700">Instructions</h4>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Instructions
                        </label>
                        <textarea
                          value={editForm.instructions}
                          onChange={(e) => setEditForm(prev => ({ ...prev, instructions: e.target.value }))}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows="3"
                          placeholder="Enter any special instructions for this session"
                        />
                      </div>
                    </div>
                  );
                })()}


              </div>

              {/* Error Message */}
              {editError && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mt-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium">{typeof editError === 'string' ? editError : 'An error occurred'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-6 border-t mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingSession(null);
                    setEditForm({});
                  }}
                  className="bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium transition-colors"
                  disabled={savingSession}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSessionChanges}
                  disabled={savingSession}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    savingSession
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                  }`}
                >
                  {savingSession ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </div>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-red-50 px-6 py-4 border-b border-red-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-red-800">Delete Session</h3>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-sm text-slate-600 mb-4">
                Are you sure you want to delete this session? This action cannot be undone.
              </p>
              <div className="bg-slate-50 rounded-lg p-3 mb-4">
                <p className="text-xs font-medium text-slate-700 mb-1">Session Details:</p>
                <p className="text-xs text-slate-600">ID: {sessionToDelete?.id?.slice(0, 8)}...</p>
                <p className="text-xs text-slate-600">Type: {sessionToDelete?.emr_name}</p>
                <p className="text-xs text-slate-600">Created: {sessionToDelete?.created_at}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-slate-50 px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSessionToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                disabled={deletingSession}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteSession}
                disabled={deletingSession}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingSession ? (
                  <div className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </div>
                ) : (
                  'Delete Session'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fields to Display Modal */}
      {showFieldsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-blue-50 px-6 py-4 border-b border-blue-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-blue-800">Select Fields to Display</h3>
                <button
                  onClick={() => setShowFieldsModal(false)}
                  className="text-blue-400 hover:text-blue-600 bg-white rounded-full p-1"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="grid grid-cols-3 gap-0">
                {/* Available Fields */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-700">Available Fields</h4>
                  <div className="border border-slate-200 rounded-lg h-64 overflow-y-auto">
                    {availableFields.map((field) => (
                      <div
                        key={field}
                        onClick={() => setSelectedAvailableField(field)}
                        className={`p-3 cursor-pointer border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors ${
                          selectedAvailableField === field ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                      >
                        <span className="text-sm text-slate-700">{formatFieldName(field)}</span>
                      </div>
                    ))}
                    {availableFields.length === 0 && (
                      <div className="p-3 text-sm text-slate-500 text-center">
                        No available fields
                      </div>
                    )}
                  </div>
                </div>

                {/* Movement Controls */}
                <div className="flex flex-col justify-center items-center">
                  <button
                    onClick={moveFieldToVisible}
                    disabled={!selectedAvailableField}
                    className={`p-1.5 rounded-t transition-colors border-b-0 ${
                      selectedAvailableField
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={moveFieldToAvailable}
                    disabled={!selectedVisibleField}
                    className={`p-1.5 rounded-b transition-colors border-t-0 ${
                      selectedVisibleField
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                </div>

                                {/* Visible Fields */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-700">Visible Fields</h4>
                  <div className="flex items-start gap-2">
                    <div className="border border-slate-200 rounded-lg h-64 overflow-y-auto flex-1">
                      {visibleFields.map((field) => (
                        <div
                          key={field}
                          onClick={() => setSelectedVisibleField(field)}
                          className={`p-3 cursor-pointer border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors ${
                            selectedVisibleField === field ? 'bg-blue-50 border-blue-200' : ''
                          }`}
                        >
                          <span className="text-sm text-slate-700">{field}</span>
                        </div>
                      ))}
                      {visibleFields.length === 0 && (
                        <div className="p-3 text-sm text-slate-500 text-center">
                          No visible fields
                        </div>
                      )}
                    </div>
                    
                    {/* Reorder Controls - Positioned outside on the right side */}
                    <div className="flex flex-col justify-center items-center">
                      <button
                        onClick={moveFieldUp}
                        disabled={!selectedVisibleField || visibleFields.indexOf(selectedVisibleField) <= 0}
                        className={`p-1.5 rounded-t transition-colors border-b-0 ${
                          selectedVisibleField && visibleFields.indexOf(selectedVisibleField) > 0
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        }`}
                        title="Move field up"
                      >
                        <ChevronUpIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={moveFieldDown}
                        disabled={!selectedVisibleField || visibleFields.indexOf(selectedVisibleField) >= visibleFields.length - 1}
                        className={`p-1.5 rounded-b transition-colors border-t-0 ${
                          selectedVisibleField && visibleFields.indexOf(selectedVisibleField) < visibleFields.length - 1
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        }`}
                        title="Move field down"
                      >
                        <ChevronDownIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-slate-50 px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={() => setShowFieldsModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveFieldDisplayConfig}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Detail Modal */}

    </div>
  );
};

const SessionsWithErrorBoundary = () => (
  <SessionsErrorBoundary>
    <Sessions />
  </SessionsErrorBoundary>
);

export default SessionsWithErrorBoundary; 